import {
  createIndirectAPIActionGenerator,
  APIRequest,
  createIndirectPaginationAPIActionGenerator,
  PaginatedAPIRequest,
} from 'utils/apidata';
import {
  PullRequest,
  RefreshPullRequest,
  PostedEntity,
  PostedComment,
  PostedCommentSnippet,
  PostedReview,
} from './models';

export type PostedEntityRequest<T = PostedEntity> = PaginatedAPIRequest<T> & {
  providerId: number;
  organizationSlug: string;
  projectName: string;
  reviewNumber: number;
};

type IndirectPostedEntityRequest<T = PostedEntity> = APIRequest<T> & {
  providerId: number;
  organizationSlug: string;
  projectName: string;
  reviewNumber: number;
};

export function getPostedEntityKey<T = PostedEntity>(
  request: PostedEntityRequest<T>
) {
  return `${request.providerId}:${request.organizationSlug}:${request.projectName}:${request.reviewNumber}`;
}

export function getIndirectPostedEntityKey<T = PostedEntity>(
  request: IndirectPostedEntityRequest<T>
) {
  return `${request.providerId}:${request.organizationSlug}:${request.projectName}:${request.reviewNumber}`;
}

export type PullRequestRequest<T = PullRequest> = APIRequest<T> & {
  uuid: string;
};

export function getIndirectKey<T = PullRequest>(
  request: PullRequestRequest<T>
) {
  return request.uuid;
}

export type ToggleReviewRequest = PullRequestRequest & {
  enabled: boolean;
  h1ReportID?: number | null;
};

export type RefreshRequest = PullRequestRequest<RefreshPullRequest> & {
  projectUUID: string;
};

export function getKeyByPullRequest(entry: PullRequest): string | undefined {
  return entry.uuid;
}

export function getKeyByPostedComment(
  entry: PostedComment
): string | undefined {
  return entry.uuid;
}

export function getKeyByPostedCommentSnippet(
  entry: PostedCommentSnippet
): string | undefined {
  return entry.comment_uuid;
}

export function getKeyByPostedReview(entry: PostedReview): string | undefined {
  return entry.uuid;
}

export const generators = {
  entries: createIndirectAPIActionGenerator<PullRequestRequest, PullRequest>({
    prefix: 'PULL_REQUESTS/ENTRIES',
    getKey: getIndirectKey,
  }),
  refresh: createIndirectAPIActionGenerator<RefreshRequest, RefreshPullRequest>(
    {
      prefix: 'PULL_REQUESTS/REFRESH',
      getKey: getIndirectKey,
    }
  ),
  toggleReview: createIndirectAPIActionGenerator<
    ToggleReviewRequest,
    PullRequest
  >({
    prefix: 'PULL_REQUESTS/TOGGLE_REVIEW',
    getKey: getIndirectKey,
  }),
  postedComments: createIndirectPaginationAPIActionGenerator<
    PostedEntityRequest,
    PostedComment
  >({
    prefix: 'PULL_REQUESTS/POSTED_COMMENTS/PAGES',
    getKey: getPostedEntityKey,
  }),
  postedCommentsEntries: createIndirectAPIActionGenerator<
    IndirectPostedEntityRequest,
    PostedComment
  >({
    prefix: 'PULLREQUESTS/POSTED_COMMENTS/ENTRIES',
    getKey: getIndirectPostedEntityKey,
  }),
  postedCommentSnippets: createIndirectPaginationAPIActionGenerator<
    PostedEntityRequest,
    PostedCommentSnippet
  >({
    prefix: 'PULL_REQUESTS/POSTED_COMMENT_SNIPPETS/PAGES',
    getKey: getPostedEntityKey,
  }),
  postedCommentSnippetsEntries: createIndirectAPIActionGenerator<
    IndirectPostedEntityRequest,
    PostedCommentSnippet
  >({
    prefix: 'PULLREQUESTS/POSTED_COMMENT_SNIPPETS/ENTRIES',
    getKey: getIndirectPostedEntityKey,
  }),
  postedReviews: createIndirectPaginationAPIActionGenerator<
    PostedEntityRequest,
    PostedReview
  >({
    prefix: 'PULL_REQUESTS/POSTED_REVIEWS/PAGES',
    getKey: getPostedEntityKey,
  }),
  postedReviewsEntries: createIndirectAPIActionGenerator<
    IndirectPostedEntityRequest,
    PostedReview
  >({
    prefix: 'PULLREQUESTS/POSTED_REVIEWS/ENTRIES',
    getKey: getIndirectPostedEntityKey,
  }),
};
